import {
  userStore,
  AnalyticsEvents,
  inviteClient,
  fileStore,
  sectionStore,
  authenticationClient,
  profilesClient,
  accessClient,
} from "@eolas-medical/core";
import { addBreadcrumb, setUser as setSentryUser } from "@sentry/react";
import Smartlook from "smartlook-client";
import { identifyUser, resetAnalytics, trackEvent } from "API/Analytics";
import { LoginWithTokenResponse } from "API/LoginTokenService";
import { errorStore } from "Stores/ErrorStore";
import OneSignal from "react-onesignal";

interface LoginData {
  email: string;
  password: string;
}

interface SignUpData extends LoginData {
  givenName: string;
  familyName: string;
}

export const login = async ({
  email,
  password,
  isFederated,
  isFederatedFeatureEnabled,
}: {
  email: string;
  password?: string;
  isFederated?: boolean;
  isFederatedFeatureEnabled?: boolean;
}) => {
  await authenticationClient.signIn({
    email,
    password,
    isFederated,
    isFederatedFeatureEnabled,
  });
};

export const onSuccessfulLogin = async (sub: string) => {
  try {
    userStore.setLoggedStatus(true);

    const user = await profilesClient.getUserProfile(sub);

    if (!user.id || !user.email) {
      addBreadcrumb({
        category: "login",
        message: "Failed login after successful Auth with cognito",
      });

      authenticationClient.signOut();

      throw new Error("getUserQuery returned null");
    }

    identifyUser(user);

    setSentryUser({ id: user.id });

    if (process.env.REACT_APP_SENTRY_ENABLED !== "false" && userStore.appUserData.appID) {
      Smartlook.identify(user.id, {
        email: user.email,
        departmentId: userStore.appUserData.appID,
      });
    }

    await createAppUserAfterInvite(user.id!, user.email!);

    const markCompletedTour = localStorage.getItem("acceptedTermsAndCond") === "true";
    localStorage.removeItem("acceptedTermsAndCond");
    if (markCompletedTour && !user.completedTour) {
      const completedTour = new Date().toISOString();
      const updatedUser = await profilesClient.updateUserProfile(user.id, {
        ...user,
        completedTour,
      });
      userStore.updateData({ user: updatedUser });
    }

    userStore.loginUser({ user });
    trackEvent(AnalyticsEvents.USER_LOGGED_IN);
    trackEvent(AnalyticsEvents.EOLAS_SESSION_START);
  } catch (e) {
    logout();
  }
};

export const createAppUserAfterInvite = async (userId: string, email: string) => {
  try {
    const inviteId = userStore.userSession.inviteId;
    const accessLinkId = userStore.userSession.accessLinkId;

    if (!inviteId && !accessLinkId) {
      return;
    }

    if (inviteId) {
      await inviteClient.createAppUser(inviteId, userId, email);
      userStore.setInviteId(null);
      trackEvent(AnalyticsEvents.ACCEPT_INVITE_DURATION);
      trackEvent(AnalyticsEvents.ACCEPT_INVITE_COMPLETED);
    }

    if (accessLinkId) {
      await accessClient.createUserAccessFromToken(accessLinkId, userId);
      userStore.setAccessLinkId(null);
      trackEvent(AnalyticsEvents.INVITE_VIA_ACCESS_LINK_SUCCESS, { userId });
    }
    OneSignal.sendTag("startedQuickDepartmentAccessDate", "null");
  } catch (error: any) {
    errorStore.captureError(error);
  }
};

export function storeClearup() {
  userStore.clearStore();
  fileStore.clearStore();
  sectionStore.clearStore();
}

export const logout = async (isSoftMode = false) => {
  try {
    await authenticationClient.signOut(isSoftMode);
  } catch (error: any) {
    userStore.setLoggedStatus(true);
    errorStore.captureError(error);
  }
};

export const onLogout = async () => {
  //remove idle timer
  try {
    setSentryUser(null);
    resetAnalytics();
    storeClearup();
    localStorage.removeItem("sessionStartedAt");
    userStore.setLoggedStatus(false);
  } catch (error: any) {
    errorStore.captureError(error);
  }
};

export async function createAccount({ email, password, givenName, familyName }: SignUpData) {
  return authenticationClient.signUp({ email, password, givenName, familyName });
}

export async function resetPasswordSendCode(email: string) {
  return authenticationClient.forgotPassword({ email });
}

export async function verifyEmail(email: string, code: string) {
  return authenticationClient.confirmSignUp({ email, code });
}

export async function resendVerificationCode(email: string) {
  return authenticationClient.resendSignUp({ email });
}

export async function forgotPasswordSubmit(
  email: string,
  password: string,
  confirmationCode: string,
) {
  return authenticationClient.forgotPasswordSubmit({
    email,
    code: confirmationCode,
    newPassword: password,
  });
}

export const loginWithToken = async (response: LoginWithTokenResponse) => {
  addBreadcrumb({
    category: "SessionContext",
    message: `loginWithToken`,
  });
  const { email, password } = response;

  await login({ email, password });

  trackEvent(AnalyticsEvents.USER_LOGGED_IN);
};
